import React from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { FetchAPI, FieldAPI, FormAPI, InputAPI, LegendsAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ReactComponent as MagnifierIcon } from '../../../resources/assets/search.svg';
import { FragmentNames } from '../../codegen';
import { AccreditationEmployeesAliases as Aliases } from '../../codegen/constants/aliases/accreditation-employees-aliases';
import BaseController from '../BaseController';

import { AccreditationSteps } from './accreditation-update-controller';
import { ActionTypes } from './update-accreditation-type';

interface Employee {
  personName: string;
  cdTaxpayerId: string;
  dsEmail: string;
  phone: string;
  nrSequence: number;
  nrSeqTypeOfLink: number;
  dsTypeOfLink: string;
  ieAction: ActionTypes,
  dsAction: string,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: FragmentNames.ACCREDITATION_EMPLOYEES })
class AccreditationEmployeesController extends BaseController {
  @Inject(Aliases.FETCH_LIST_EMPLOYEES)
  private fetchListEmployees: FetchAPI;
  @Inject(Aliases.FETCH_EMPLOYEE)
  private fetchEmployee: FetchAPI;
  @Inject(Aliases.FORM_EMPLOYEE)
  private formEmployee!: FormAPI;
  @Inject(Aliases.FIELD_NAME)
  private fieldName: FieldAPI;
  @Inject(Aliases.FIELD_TYPE_OF_LINK)
  private fieldTypeOfLink: FieldAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.INPUT_FILTER)
  private inputFilter: InputAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  @OnAfterFetch(Aliases.FETCH_LIST_EMPLOYEES)
  onAfterFetchListEmployees(event: FetchEvent): void {
    const { data } = event;
    if (isEmptyArrayOrObject(data)) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    } else {
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    }
  }

  @OnMount(Aliases.LAYOUT_EMPLOYEES)
  onMountLayout(): void {
    const { nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.context.set({ nrSeqProvider, nrSeqAccreditation, nrSeqTypeAccreditation });
    this.fetchListEmployees.forceFetch();
  }

  @OnChange(Aliases.FORM_EMPLOYEE)
  onChangeForm(): void {
    this.formEmployee.setState({ forceValidate: true });
  }

  @OnCancel(Aliases.FORM_EMPLOYEE)
  onCancelForm(): void {
    this.cleanForm();
  }

  @OnSubmit(Aliases.FORM_EMPLOYEE)
  onSubmitFormEmployee(event: FormAPI): void {
    const body = event.record;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.formEmployee.setState({ loading: true });
    HttpService.post(Endpoint.POST.EMPLOYEE, { nrSeqProvider, nrSeqAccreditation }, { body })
      .then(() => {
        this.cleanForm();
        this.formEmployee.setState({ loading: false });
        this.fetchListEmployees.forceFetch();
      }).catch((response) => {
        this.formEmployee.setState({ loading: false });
        ToastService.open(response);});
  }

  private cleanForm(): void {
    this.formEmployee.setRecord({});
    this.formEmployee.setData({});
    this.fieldName.setState({ valid: true, showValidations: false });
    this.fieldTypeOfLink.setState({ showValidations: false });
  }

  @OnMount(Aliases.PANEL_CONTENT_FORM_EMPLOYEE)
  onMountForm(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.EMPLOYEES);
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetch(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.EMPLOYEES);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  @OnMount(Aliases.INPUT_FILTER)
  onMountFilter(): void {
    this.inputFilter.setExternalAction(<MagnifierIcon className="c-module-selector__search-icon" />);
  }

  @OnChange(Aliases.INPUT_FILTER)
  onEditFilter(event: ChangeEvent<Employee>): void {
    const params = this.fetchListEmployees.getParameters();
    const desc = event.value;
    this.fetchListEmployees.setParameters({ ...params, desc });
    this.fetchListEmployees.forceFetch();
  }

  @HPMSOnCreateMenu(Aliases.HPMS_EMPLOYEE_CARD)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @HPMSOnCreateOption(Aliases.HPMS_EMPLOYEE_CARD)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable } = data;
    const show = {
      [OptionKey.EDIT]: editable === 'S',
      [OptionKey.DELETE]: editable === 'S',
    };
    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.HPMS_EMPLOYEE_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Employee>): void {
    const { data, key } = event;
    const { nrSequence } = data;
    const action = {
      [OptionKey.EDIT]: () => this.editEmployee(nrSequence),
      [OptionKey.DELETE]: () => this.deleteEmployee(nrSequence),
    };
    if (action[key]) {
      action[key]();
    }
  }

  private editEmployee(nrSequence: number): void {
    this.cleanForm();
    this.fetchEmployee.setParameters({ nrSequence });
    this.fetchEmployee.forceFetch();
  }

  private deleteEmployee(nrSequence: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.delete(Endpoint.DELETE.EMPLOYEE, { nrSeqProvider, nrSequence, nrSeqAccreditation })
      .then(() => {
        this.cleanForm();
        this.fetchListEmployees.forceFetch();
      }).catch((response) => {
        ToastService.open(response);
        this.fetchListEmployees.forceFetch();
      });
  }
}

export {
  AccreditationEmployeesController,
};
